import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Grid, MenuItem, useMediaQuery, useTheme } from "@mui/material";

import BarekamPhoneInput from "../../../auth/phone-input/phone-input";

import { DimensionsContainer, RequestFormContainer } from "./styled.components";
import { Message, MessageSections } from "../../../components/message/message";
import BarekamButton from "../../../components/button/barekam-button";
import BarekamInput from "../../../components/input/barekam-input";
import { BarekamSelect } from "../../../components/select/select";
import { AuthButtons } from "../../../components/button";
import BarekamText from "../../../components/text";

import TypeSelectionBlock from "../../../auth/TypeSelectionBlock";
import { UserTypeEnum } from "../../../shared/enum/user-type.enum";

import { getMessage, initToast } from "../../../shared/helper-functions";

import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { getCountries } from "../../../../redux/slices/static-data.slice";
import axiosInstance from "../../../../router/interceptor";

const initialData: RequestFormProps = {
    firstName: "",
    email: "",
    phoneNumber: 0,
    senderCountry: 0,
    companyPersonFirstName: "",
}

type RequestFormProps = {
    firstName: string,
    email: string,
    phoneNumber: number,
    weight?: string,
    calculateWidthCm?: string,
    calculateHeightCm?: string,
    calculateLengthCm?: string,
    description?: string,
    senderCountry: number,
    entityName?: string,
    companyPhoneNumber?: string,
    companyPersonFirstName?: string,
}

let count = 0;

const RequestForm = ({ setShow }: { setShow: Dispatch<SetStateAction<boolean>> }) => {
    const [data, setData] = useState(initialData);
    const [errors, setErrors] = useState<{ [key: string]: string }>({
        firstName: "",
        email: "",
        phoneNumber: "",
        weight: "",
        description: "",
        senderCountry: ""
    });
    const { messageCache, languageId } = useAppSelector(
        (state) => state.messagesReducer,
    );
    const { countries } = useAppSelector(state => state.staticDataReducer);
    const [selectedType, setSelectedType] = useState<UserTypeEnum | undefined>(1);
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const matchesPhone =
        useMediaQuery(theme.breakpoints.between(0, 1200)) ||
        window.innerHeight <= 500;

    useEffect(() => {
        if (count === 0) {
            dispatch(getCountries());
        }
        count++;
    }, [count])

    const onChange = (field: string, value: any) => {
        setData((prevState) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            prevState[field] = value;
            return { ...prevState };
        });

        if (errors[field]) {
            setErrors((prevState) => {
                delete prevState[field];
                return prevState;
            });
        }
    }

    const getRenderValue = (countryId: number) => {
        const selectedCountry = countryId
            ? countries.find(country => country.countryId === countryId)
            : null;

        return selectedCountry ? selectedCountry.name[languageId] : getMessage('country', MessageSections.MAIN, messageCache, languageId);
    }

    const submit = async () => {
        Object.keys(data).map(key => {
            if (!data[key as keyof typeof data]) {
                setErrors((prevState) => {
                    return { ...prevState, [key]: "field_is_required" }
                });
            }
        })

        const finalData = data;
        if(finalData?.companyPersonFirstName && !finalData.firstName){
            finalData.firstName = finalData.companyPersonFirstName;
        }

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const result = await axiosInstance.post(
                `${API_URL}/wholesale`,
                { ...finalData }
            );
            if (result.status === 201) {
                initToast(
                    getMessage('form_submited', MessageSections.MAIN, messageCache, languageId),
                    'success'
                );
                setTimeout(() => setShow(false), 1000);
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <RequestFormContainer>
            <div className="type-container"><TypeSelectionBlock setSelectedType={setSelectedType} selectedType={selectedType} hideTitle /></div>
            {selectedType === UserTypeEnum.PHYSICAL_PERSON ? (
                <BarekamInput
                    key={'first-name'}
                    customInputVariant={"standard"}
                    placeholder={getMessage('first_name', MessageSections.LOGIN, messageCache, languageId)}
                    value={data?.firstName}
                    onChange={(value) => onChange('firstName', value.target.value)}
                    type={'text'}
                    error={!!errors['firstName']}
                    errorMessage={errors['firstName'] && getMessage(errors['firstName'], MessageSections.LOGIN, messageCache, languageId)}
                    width={'100%'}
                    required
                />
            ) : (
                <>
                    <BarekamInput
                        key={'entity-name'}
                        customInputVariant={"standard"}
                        placeholder={getMessage('entity_name', MessageSections.LOGIN, messageCache, languageId)}
                        value={data?.entityName}
                        onChange={(value) => onChange('entityName', value.target.value)}
                        type={'text'}
                        error={!!errors['entityName']}
                        errorMessage={errors['entityName'] && getMessage(errors['entityName'], MessageSections.LOGIN, messageCache, languageId)}
                        width={'100%'}
                    />
                    <BarekamInput
                        key={'company-person-first-name'}
                        customInputVariant={"standard"}
                        placeholder={getMessage('company_person_first_name', MessageSections.LOGIN, messageCache, languageId)}
                        value={data?.companyPersonFirstName}
                        onChange={(value) => onChange('companyPersonFirstName', value.target.value)}
                        type={'text'}
                        error={!!errors['companyPersonFirstName']}
                        errorMessage={errors['companyPersonFirstName'] && getMessage(errors['companyPersonFirstName'], MessageSections.LOGIN, messageCache, languageId)}
                        width={'100%'}
                        required
                    />
                </>
            )}
            <BarekamInput
                key={'email'}
                customInputVariant={"standard"}
                placeholder={getMessage('email', MessageSections.USER, messageCache, languageId)}
                value={data?.email}
                onChange={(value) => onChange('email', value.target.value)}
                type='email'
                error={!!errors['email']}
                errorMessage={errors['email'] && getMessage(errors['email'], MessageSections.LOGIN, messageCache, languageId)}
                width={'100%'}
                required
            />
            <BarekamPhoneInput
                key={"phoneNumber"}
                value={""}
                placeholder={getMessage(
                    "phone_number",
                    MessageSections.LOGIN,
                    messageCache,
                    languageId,
                )}
                onChange={(value: number) => onChange("phoneNumber", value)}
                error={!!errors["phoneNumber"]}
                errorMessage={
                    errors["phoneNumber"] &&
                    getMessage(
                        errors["phone"],
                        MessageSections.LOGIN,
                        messageCache,
                        languageId,
                    )
                }
            />
            <BarekamSelect
                label={<BarekamText variant={'1rem'}>
                    <Message messageKey={'sender_country'} sectionId={MessageSections.CALCULATE} />
                </BarekamText>}
                variant={'outlined'}
                displayEmpty={!data.senderCountry}
                renderValue={(countryId) => getRenderValue(countryId as number)}
                value={data.senderCountry}
                type={"standard"}
                onChange={(event) => onChange('senderCountry', event.target.value as number)}
                MenuProps={{
                    PaperProps: {
                        square: true,
                        sx: {
                            '& .MuiList-root': {
                                padding: 0,
                            }
                        }
                    }
                }}
                error={!!errors['senderCountry']}
                errorMessage={errors['senderCountry'] && getMessage(errors['senderCountry'], MessageSections.LOGIN, messageCache, languageId)}
                required
            >

                {countries
                    .map(country =>
                        <MenuItem value={country.countryId}
                            key={country.countryId}>
                            {country.name[languageId]}
                        </MenuItem>
                    )}
            </BarekamSelect>
            <BarekamInput
                key={'weight'}
                customInputVariant={"standard"}
                placeholder={getMessage('weight', MessageSections.ORDERS, messageCache, languageId)}
                value={data.weight || ''}
                onChange={(event) => onChange('weight', +event.target.value)}
                type={'number'}
                error={!!errors['weight']}
                errorMessage={errors['weight'] && getMessage(errors['weight'], MessageSections.LOGIN, messageCache, languageId)}
            />

            <DimensionsContainer>
                <Grid item xs={matchesPhone ? 12 : 3}>
                    <BarekamInput 
                        value={data.calculateLengthCm || ''}
                        placeholder={getMessage('calculate_length_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        label={getMessage('calculate_length_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        type={"number"}
                        customInputVariant={"standard"}
                        width={'100%'}
                        error={!!errors['calculateLengthCm']}
                        errorMessage={errors['calculateLengthCm'] && getMessage(errors['calculateLengthCm'], MessageSections.LOGIN, messageCache, languageId)}
                        onChange={(event) => onChange('calculateLengthCm', +event.target.value)} />
                </Grid>

                <Grid item xs={matchesPhone ? 12 : 3}>
                    <BarekamInput value={data.calculateWidthCm || ''}
                        placeholder={getMessage('calculate_width_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        label={getMessage('calculate_width_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        type={"number"}
                        customInputVariant={"standard"}
                        width={'100%'}
                        error={!!errors['calculateWidthCm']}
                        errorMessage={errors['calculateWidthCm'] && getMessage(errors['calculateWidthCm'], MessageSections.LOGIN, messageCache, languageId)}
                        onChange={(event) => onChange('calculateWidthCm', +event.target.value)} />
                </Grid>

                <Grid item xs={matchesPhone ? 12 : 3}>
                    <BarekamInput value={data.calculateHeightCm || ''}
                        type={"number"}
                        placeholder={getMessage('calculate_height_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        label={getMessage('calculate_height_cm', MessageSections.CALCULATE, messageCache, languageId)}
                        customInputVariant={"standard"}
                        width={'100%'}
                        error={!!errors['calculateHeightCm']}
                        errorMessage={errors['calculateHeightCm'] && getMessage(errors['calculateHeightCm'], MessageSections.LOGIN, messageCache, languageId)}
                        onChange={(event) => onChange('calculateHeightCm', +event.target.value)} />
                </Grid>
            </DimensionsContainer>
            <BarekamInput key={'description'}
                customInputVariant={"standard"}
                placeholder={getMessage('description', MessageSections.MAIN, messageCache, languageId)}
                value={data?.description}
                onChange={(value) => onChange('description', value.target.value)}
                type={'text'}
                error={!!errors['description']}
                errorMessage={errors['description'] && getMessage(errors['description'], MessageSections.LOGIN, messageCache, languageId)}
                width={'100%'}
            />
            <BarekamButton type={AuthButtons.MAIN} size={"l"} onClick={submit}>
                <BarekamText variant={"l"} fontWeight={700} align={"center"}>
                    <Message
                        messageKey={"send"}
                        sectionId={MessageSections.MAIN}
                    />
                </BarekamText>
            </BarekamButton>
        </RequestFormContainer>
    )
}

export default RequestForm;